import { useEffect, useMemo, useState } from 'react';

import { useRunJournalEvents } from '@/modules/automation-run/hooks/useRunJournalEvents';
import {
  resolveExternalCalls,
  type IResolvedExternalCall,
} from '@/modules/automation-run/utils/external-call-visibility';
import type { V1CodeManifest, V1Run } from '@/generated/hooks/jeeves/models';

import type { TStepRunStatus } from '../utils/step-run-status';

import { type IStepRunIO, useManifestStepIO } from './use-manifest-step-io';
import { useManifestStepStatuses } from './use-manifest-step-statuses';

interface IUseFocusedTestRunStatusParams {
  automationId: string;
  runId: string | undefined;
  manifest: V1CodeManifest | undefined;
  run: V1Run | undefined;
}

export type { IStepRunIO };

export interface IFocusedTestRunStatus {
  /** Per-step status (by manifest `V1Step.number`) for the focused run. */
  statuses: ReadonlyMap<number, TStepRunStatus>;
  /** External calls the running step has finished so far. */
  runningStepCalls: IResolvedExternalCall[];
  /** Each executed step's real inputs/output/duration, by `V1Step.number`. */
  stepIO: ReadonlyMap<number, IStepRunIO>;
  /** True until the run's event history settles, success or failure. */
  isLoadingEvents: boolean;
  /** Set when the event history failed to load, or the live tail gave up. */
  eventsError: Error | null;
  /**
   * True when a finished run yielded no events, so `statuses` and `stepIO` are
   * empty for lack of data rather than because the run did nothing. Covers both
   * a failed fetch and a successful one that returned an empty list.
   */
  stepDetailsUnavailable: boolean;
  /**
   * The focused run, kept live via `run_update` events on the same bishop
   * journal stream `events`/`statuses` already consume. The one-shot `GetRun`
   * snapshot passed in as `run` never updates on its own, so without this a
   * finished run's steps show done while the status badge and outputs stay
   * stuck on whatever state was fetched at focus time.
   */
  run: V1Run | undefined;
}

/**
 * Per-step status and I/O for the focused test run, live or historical alike
 * — reuses the same bishop-journal hook `RunPage` uses for real runs
 * (`useRunJournalEvents`, `bishop.events.v1.EventsService.ListRunEvents`), keyed
 * to the reconstructed manifest from `useTestRunManifest` (never the live
 * in-editor manifest).
 */
export function useFocusedTestRunStatus({
  automationId,
  runId,
  manifest,
  run,
}: IUseFocusedTestRunStatusParams): IFocusedTestRunStatus {
  const steps = useMemo(
    () => (manifest?.steps ?? []).filter(step => step.entry_function?.name),
    [manifest],
  );

  const mainBounds = useMemo(() => {
    const loc = manifest?.main?.location;
    const startLine = loc?.start_position?.line;
    const endLine = loc?.end_position?.line;
    if (startLine == null || endLine == null) return undefined;
    return { startLine, endLine };
  }, [manifest]);

  // Seeded from the one-shot GetRun snapshot, then kept current by
  // `onRunStateUpdate` below — reset whenever a new snapshot arrives (i.e.
  // the focused run itself changed) so a stale live state never survives a
  // run switch.
  const [liveRun, setLiveRun] = useState(run);
  // eslint-disable-next-line no-restricted-syntax -- reason: syncs local state to a prop change (new GetRun snapshot on run switch), not a data fetch
  useEffect(() => {
    setLiveRun(run);
  }, [run]);

  const {
    events,
    runningStep,
    runningStepExternalCalls,
    isLoading: isLoadingEvents,
    error: eventsError,
  } = useRunJournalEvents({
    automationId,
    runId,
    mainBounds,
    runState: liveRun?.state,
    totalSteps: steps.length,
    onRunStateUpdate: setLiveRun,
  });

  // Zero events is zero evidence, however we got there. The reported case was a
  // plain `200 { "run_events": [] }` — no error to key off — which still left
  // every step green with nothing behind it.
  const eventsUnavailable = events.length === 0;

  // Only worth telling the user about once the fetch has settled and the run is
  // over. A live run with no steps yet is just early, not broken.
  const isRunOver = !!(
    liveRun?.state?.completed ||
    liveRun?.state?.failed ||
    liveRun?.state?.stopped
  );
  const stepDetailsUnavailable =
    !isLoadingEvents && eventsUnavailable && isRunOver;

  // Shared with the AOP map and the run page, so no two surfaces can disagree
  // about which step is running.
  const statuses = useManifestStepStatuses({
    manifest,
    events,
    runState: liveRun?.state,
    eventsUnavailable,
  });

  const errorDescription =
    liveRun?.state?.failed?.description ??
    liveRun?.state?.awaiting_guidance?.description;

  // Shared with the AOP map's run panel, so no two surfaces disagree about
  // what a step's inputs and output were.
  const stepIO = useManifestStepIO({
    manifest,
    events,
    statuses,
    errorDescription,
    runningStepExternalCalls,
    runningStep,
  });

  const runningStepCalls = useMemo(
    () => resolveExternalCalls(runningStepExternalCalls),
    [runningStepExternalCalls],
  );

  return {
    statuses,
    runningStepCalls,
    stepIO,
    run: liveRun,
    isLoadingEvents,
    eventsError,
    stepDetailsUnavailable,
  };
}
